import { createContext } from "react";

import type { AppSettings } from "@/api/settings";
import type { Role } from "@/constants/permissions";

export type SessionUser = {
  id: number;
  name: string;
  email: string;
  role: Role;
  /**
   * The client company this principal belongs to, or null.
   *
   * Only a `company` principal carries one, and a check constraint on `users`
   * makes a company principal without one unrepresentable. It is read here for a
   * single purpose: `consoleScope` turns this session into a scope, and a missing
   * value has to read as "nothing" rather than "everything".
   */
  companyId: number | null;
  locale: "ar" | "en";
  numeralSystem: "arabic-indic" | "western";
  timeZone: string;
  avatarKey: string | null;
  /** Personal photo, or null when the record has none. */
  photoUrl: string | null;
};

export type Session = {
  sessionId: number;
  user: SessionUser;
  settings: AppSettings;
};

export type SessionState =
  /** The first `GET /auth/session` has not answered yet. */
  | { status: "loading" }
  | { status: "signedIn"; session: Session }
  | { status: "signedOut" };

export type SessionContextValue = SessionState & {
  /** Re-reads the session from the API. Called after signing in. */
  refresh: () => Promise<void>;
  /** Ends the session locally immediately; revokes on the server in the background. */
  signOut: () => void;
};

export const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * Last signed-in session kept across the sign-out render.
 *
 * Every `useSession()` caller subscribes to this context. When status flips to
 * `signedOut`, React re-renders those callers in the same pass as
 * `RequireSession`'s `<Navigate to="/login" />`. Throwing from `useSession`
 * aborts that navigate and leaves the shell stuck with a console error — which
 * is exactly what the logout button was doing.
 */
export let lastSignedInSession: Session | null = null;

export function setLastSignedInSession(session: Session | null): void {
  lastSignedInSession = session;
}
